import React from "react";
import type { Theme } from "../lib/theme";
import type { Division } from "../types";
import type { DivTotals, FinalTotals } from "../lib/calculations";
import * as Icon from "../components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface ScopeData {
  introduction: string;
  divisions: Array<{
    divisionId: string;
    narrative: string;
    inclusions: string[];
    standardExclusions?: string[];
  }>;
  alternatesNarrative?: string;
}

interface ScopeTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  divisions: Division[];
  divTotals: DivTotals;
  handleGenerateScope: () => void;
  handleDownloadScope: () => void;
  scopeLoading: boolean;
  scopeData: ScopeData | null;
  scopeExclusions: Record<string, string>;
  setScopeExclusions: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  projectName: string;
  address: string;
  buildingArea: number;
  final: FinalTotals;
  fmtSF: (n: number) => string;
  fmt: (n: number) => string;
  bidPartners: { id: string; name: string }[];
  projectExclusions: string[];
}

export default function ScopeTab({
  T, isMobile, isTablet, divisions, divTotals,
  handleGenerateScope, handleDownloadScope, scopeLoading, scopeData,
  scopeExclusions, setScopeExclusions, projectName, address, buildingArea,
  final, fmtSF, fmt, bidPartners, projectExclusions,
}: ScopeTabProps) {
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const activeDivs = divisions.filter(div => (divTotals[div.id]?.total || 0) !== 0);

  return (
    <div className="fade-in" style={{ padding: isMobile ? "14px 16px" : isTablet ? "20px 28px" : "32px 48px", maxWidth: 920, margin: "0 auto" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.borderAccent, marginBottom: 4 }}>Scope of Work</div>
          <div style={{ fontSize: 11, color: T.textWeak, maxWidth: 480 }}>
            AI-generated from your estimate. Review the narrative, then add division-specific exclusions before downloading the contract document.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={handleGenerateScope} disabled={scopeLoading}
            style={{ padding: "8px 18px", fontSize: 12, fontWeight: 600, fontFamily: FONT, border: `1px solid ${T.borderAccent}`, borderRadius: 3,
              background: scopeLoading ? T.bgInput : T.bgAccentSubtle, color: scopeLoading ? T.textMuted : T.borderAccent,
              cursor: scopeLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {scopeLoading ? <><Icon.Hourglass size={13} /> Generating…</> : <><Icon.Sparkle size={13} /> Generate Scope</>}
          </button>
          {scopeData && (
            <button onClick={handleDownloadScope}
              style={{ padding: "8px 18px", fontSize: 12, fontWeight: 600, fontFamily: FONT, border: "none", borderRadius: 3,
                background: T.borderAccent, color: T.textOnGold, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              ⬇ Download .docx
            </button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!scopeData && !scopeLoading && (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "60px 40px", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 16, opacity: .3 }}><Icon.FileText size={36} /></div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Generate your Scope of Work</div>
          <div style={{ fontSize: 12, color: T.textWeak, maxWidth: 400, margin: "0 auto", lineHeight: 1.7 }}>
            Click "Generate Scope" to have Claude write a professional scope of work document based on your estimate — organized by CSI division with inclusions, exclusion fields, and a cost summary.
          </div>
          {activeDivs.length === 0 && (
            <div style={{ marginTop: 20, fontSize: 11, color: T.textMuted, background: T.bgInput, padding: "8px 16px", borderRadius: 3, display: "inline-block" }}>
              Add line items to your estimate first
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {scopeLoading && (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "60px 40px", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.borderAccent, fontWeight: 600, marginBottom: 8 }}>Claude is writing your Scope of Work…</div>
          <div style={{ fontSize: 11, color: T.textWeak }}>This usually takes 15–30 seconds depending on your estimate size.</div>
        </div>
      )}

      {/* Document preview */}
      {scopeData && !scopeLoading && (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, overflow: "hidden" }}>
          {/* Document header */}
          <div style={{ padding: "32px 40px 24px", borderBottom: `2px solid ${T.borderAccent}`, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".22em", color: T.borderAccent, textTransform: "uppercase", marginBottom: 10 }}>Scope of Work</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, letterSpacing: "-.01em", marginBottom: address ? 4 : 6 }}>{projectName || "Construction Project"}</div>
            {address && <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{address}</div>}
            <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 20 }}>Prepared: {today}</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", marginBottom: 4 }}>TOTAL ESTIMATE</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.positive }}>{fmt(final.total)}</div>
              </div>
              {buildingArea > 0 && <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", marginBottom: 4 }}>BUILDING AREA</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.textSecondary }}>{buildingArea.toLocaleString()} SF</div>
              </div>}
              {buildingArea > 0 && <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", marginBottom: 4 }}>COST PER SF</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.positive }}>{fmtSF(final.costPerSF)}</div>
              </div>}
              {bidPartners.length > 0 && <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", marginBottom: 4 }}>TRADE PARTNERS</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.textSecondary }}>{bidPartners.length}</div>
              </div>}
            </div>
          </div>

          {/* Project Overview */}
          <div style={{ padding: "28px 40px", borderBottom: `1px solid ${T.borderPrimary}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".16em", color: T.borderAccent, textTransform: "uppercase", marginBottom: 12 }}>Project Overview</div>
            <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.8 }}>{scopeData.introduction}</div>
          </div>

          {/* Division sections */}
          {scopeData.divisions.map(divResult => {
            const divData = divisions.find(d => d.id === divResult.divisionId);
            if (!divData) return null;
            const divTotal = divTotals[divResult.divisionId]?.total || 0;
            const excl = scopeExclusions[divResult.divisionId] || "";
            return (
              <div key={divResult.divisionId} style={{ borderBottom: `1px solid ${T.borderPrimary}` }}>
                <div style={{ padding: "20px 40px 0", display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", color: T.textWeak, marginBottom: 4 }}>{divData.code}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, letterSpacing: "-.01em" }}>{divData.name}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.positive }}>{fmt(divTotal)}</div>
                    {buildingArea > 0 && <div style={{ fontSize: 10, color: T.textWeak }}>{fmtSF(divTotal / buildingArea)}</div>}
                  </div>
                </div>
                <div style={{ padding: "12px 40px 0" }}>
                  <div style={{ fontSize: 12, color: T.textTertiary, lineHeight: 1.75 }}>{divResult.narrative}</div>
                </div>

                {/* Inclusions */}
                <div style={{ padding: "16px 40px 0" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".14em", color: "#4ade80", textTransform: "uppercase", marginBottom: 10 }}>Inclusions</div>
                  <ul style={{ margin: 0, padding: "0 0 0 18px", listStyle: "disc" }}>
                    {divResult.inclusions.map((inc, ii) => (
                      <li key={ii} style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.7, marginBottom: 3 }}>{inc}</li>
                    ))}
                  </ul>
                </div>

                {/* Exclusions — editable */}
                <div style={{ padding: "16px 40px 24px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".14em", color: "#fc8181", textTransform: "uppercase", marginBottom: 6 }}>Exclusions</div>
                  <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 8 }}>Enter one exclusion per line. These will appear in the downloaded document.</div>
                  <textarea
                    value={excl}
                    onChange={e => setScopeExclusions(prev => ({ ...prev, [divResult.divisionId]: e.target.value }))}
                    placeholder={`e.g. Permit fees and associated costs\nOwner-furnished equipment and materials\nHazardous material abatement`}
                    rows={4}
                    style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 3,
                      color: T.textSecondary, fontFamily: FONT, fontSize: 12, lineHeight: 1.6, padding: "10px 12px", resize: "vertical" as any, outline: "none" }}
                    onFocus={e => (e.currentTarget.style.borderColor = T.borderAccent)}
                    onBlur={e => (e.currentTarget.style.borderColor = T.borderSecondary)}
                  />
                  {divResult.standardExclusions && divResult.standardExclusions.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 6 }}>SUGGESTIONS — click to add:</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {divResult.standardExclusions.map((sug, si) => (
                          <button key={si} onClick={() => {
                            setScopeExclusions(prev => {
                              const cur = prev[divResult.divisionId] || "";
                              const lines = cur.trim().split("\n").filter(Boolean);
                              if (lines.includes(sug)) return prev;
                              return { ...prev, [divResult.divisionId]: [...lines, sug].join("\n") };
                            });
                          }}
                            style={{ padding: "3px 9px", background: T.bgInput, border: `1px solid ${T.borderSecondary}`,
                              borderRadius: 10, fontSize: 10, color: T.textTertiary, cursor: "pointer", fontFamily: FONT,
                              transition: "border-color .15s,color .15s" }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderAccent; e.currentTarget.style.color = T.borderAccent; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = T.borderSecondary; e.currentTarget.style.color = T.textTertiary; }}>
                            + {sug}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Alternates section */}
          {scopeData.alternatesNarrative && (
            <div style={{ padding: "24px 40px", borderBottom: `1px solid ${T.borderPrimary}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".16em", color: T.borderAccent, textTransform: "uppercase", marginBottom: 12 }}>Alternates</div>
              <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.8, whiteSpace: "pre-line" }}>{scopeData.alternatesNarrative}</div>
            </div>
          )}

          {/* Project Exclusions */}
          {projectExclusions.length > 0 && (
            <div style={{ padding: "24px 40px", borderBottom: `1px solid ${T.borderPrimary}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".16em", color: "#fc8181", textTransform: "uppercase", marginBottom: 12 }}>General Exclusions</div>
              <ul style={{ margin: 0, padding: "0 0 0 18px", listStyle: "disc" }}>
                {projectExclusions.map((exc, i) => (
                  <li key={i} style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.7, marginBottom: 3 }}>{exc}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Disclaimer */}
          <div style={{ padding: "20px 40px", background: T.bgApp }}>
            <div style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.7, textAlign: "center", fontStyle: "italic" }}>
              This Scope of Work describes the general scope of the construction estimate as of {today}. It does not constitute a construction contract. Final contract documents shall govern in all cases.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
